"use client";

import React from "react";

import {
  ColumnDef,
  flexRender,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type PaginationState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { ChevronLeft, ChevronRight } from "lucide-react";

interface DataTableProps<Data, Value> {
  columns: ColumnDef<Data, Value>[];
  data: Data[];
}

export function DataTable<Data, Value>({
  columns,
  data,
}: DataTableProps<Data, Value>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 21,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  const totalRows = table.getFilteredRowModel().rows.length;
  const startRow = pagination.pageIndex * pagination.pageSize + 1;
  const endRow = Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    totalRows,
  );

  const pages = generatePages({
    pageIndex: pagination.pageIndex,
    totalPages: Math.ceil(totalRows / pagination.pageSize),
  });

  return (
    <Card className="overflow-hidden bg-transparent p-4">
      <div className="relative w-full overflow-auto">
        <Table className="min-w-[800px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="bg-background flex flex-col items-center justify-center gap-6 rounded-md p-3 sm:flex-row sm:justify-between">
        <p className="text-foreground text-xs">
          Showing {startRow} - {endRow} out of {totalRows}
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="size-6"
            aria-label="Previous page"
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          {pages.map((page, index) => (
            <Button
              key={index}
              size="icon"
              variant={page === pagination.pageIndex ? "default" : "ghost"}
              onClick={() =>
                typeof page === "number" && table.setPageIndex(page)
              }
              disabled={typeof page !== "number"}
              className="size-6"
            >
              {typeof page === "number" ? page + 1 : "..."}
            </Button>
          ))}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="size-5"
            aria-label="Next page"
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function generatePages({
  pageIndex,
  totalPages,
}: {
  pageIndex: number;
  totalPages: number;
}) {
  const pages = [];
  const sidePages = 2; // Number of pages to show on each side of the current page

  // Always include the first page
  pages.push(0);

  // Add ellipsis if needed
  if (pageIndex > sidePages + 1) {
    pages.push("...");
  }

  // Add middle pages
  const startPage = Math.max(pageIndex - sidePages, 1);
  const endPage = Math.min(pageIndex + sidePages, totalPages - 2);

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis if needed
  if (pageIndex < totalPages - sidePages - 2) {
    pages.push("...");
  }

  // Always include the last page
  if (totalPages > 1) {
    pages.push(totalPages - 1);
  }

  return pages;
}
